<template>
	<div>
		<BaseHeader :title="$t('edit_insurance').toString()" />

		<div v-if="isUserAllowedInLevel1 || isAuthenticatedUserInGroupAdmin" class="flex space-x-4">
			<div class="flex flex-grow" v-if="!isLoading">
				<CompanyProfileForm
					ref="CompanyProfileForm"
					v-model="model"
					:company-type="companyType"
					:is-edit-modus="true"
					:show-header="false"
					class="flex-grow"
					type="insurance"
					@save="companySave"
					:send-to-api="false"
				>
					<template v-if="!isSystemInsurance" slot="buttons">
						<BaseButton @click="addToSystem(false)" class="mr-4">{{ $t('do_not_copy') }}</BaseButton>
						<BaseButton @click="addToSystem(true)" class="mr-4">{{ $t('copy_to_data') }}</BaseButton>
					</template>
				</CompanyProfileForm>

				<div v-if="getCompanies && !isSystemInsurance" class="px-8 mt-8 w-1/4 text-left">
					<div class="text-lg font-bold">{{ $t('similar_insurance_name') }}</div>
					<div v-for="company in getCompanies" :key="company.id">
						<div
							@click="$router.push({ name: 'insurancesprofile', params: { id: company.id } })"
							class="cursor-pointer"
							:class="{
								'text-yellow-600': company.distance > 80 && company.distance < 95,
								'text-red-600': company.distance > 95,
							}"
						>
							{{ company.distance }}% - {{ company.name }}
						</div>
					</div>
				</div>
			</div>
			<LoadingSpinner v-else />
		</div>
		<div v-else>
			<Forbidden />
		</div>
	</div>
</template>

<script>
import CompanyProfileForm from '@/components/forms/CompanyProfileForm';
import { InsurancesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseHeader from '@/components/navigation/BaseHeader';
import JaroWinkler from '@/helpers/jaro_winkler';
import RouteWatcher from '@/mixins/RouteWatcher';
import Formatter from '@/mixins/Formatter';
import Validate from '@/mixins/Validate';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

export default {
	components: { LoadingSpinner, BaseHeader, BaseButton, Forbidden, CompanyProfileForm },
	name: 'InsuranceProfile',
	mixins: [Authentication, RouteWatcher, Formatter, Validate],
	data() {
		return {
			model: {
				bankAccount: {},
				city: {},
			},
			datPrices: null,
			showDatPrices: false,
			usergroups: null,
			companyType: 'Versicherung',
			companies: null,
			isLoading: false,
		};
	},
	computed: {
		getCompanies() {
			return this.companies
				?.map(company => {
					return { ...company, distance: this.getSimilarity(company.name) };
				})
				.filter(company => company.distance > 50)
				.sort((a, b) => {
					return b.distance - a.distance;
				});
		},
		isNameAlreadyUsed() {
			return this.getCompanies?.find(company => company.name === this.model.name);
		},
		isSystemInsurance() {
			return this.model?.is_system_insurance === 1;
		},
	},
	methods: {
		addToSystem(addToSystem) {
			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
			}
			this.model.is_system_insurance = addToSystem;

			if (!addToSystem) {
				this.model.is_rejected = true;
			}

			InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.insurancesIdPut(this.model.id, this.model)
				.then(response => {
					this.$store.commit('setConfirmLeaving', false);
					if (!addToSystem) {
						this.$swal(this.$t('general.success').toString(), this.$t('insurance_not_added').toString(), 'success');
					} else {
						this.$swal(this.$t('general.success').toString(), this.$t('insurance_saved_to_data').toString(), 'success');
					}
					this.$router.push({ name: 'administrate_companies', params: { type: 'insurance' } });
				});
		},
		onRouteChange() {
			this.isLoading = true;
			const company = this.getUser.person?.company;
			const id = this.$route.params?.id || company?.company_id || company?.id;
			this.getCompanyProfileData(id);
		},
		getSimilarity(companyName) {
			const distance = JaroWinkler.Distance(companyName, this.model.name);
			return this.formatNumber(Math.round(distance * 100), 0);
		},
		checkNameAlreadyUsed() {
			if (this.isNameAlreadyUsed) {
				this.$swal(this.$t('general.attention').toString(), this.$t('insurance_name_exists').toString(), 'warning');
			}
		},
		companySave() {
			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
			}
			InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.insurancesIdPut(this.model.id, this.model)
				.then(response => {
					this.$store.commit('setConfirmLeaving', false);
					this.$swal(this.$t('general.success').toString(), this.$t('insurance_saved').toString(), 'success');
				});
		},
		updateInsurances() {
			InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.insurancesGet(0)
				.then(response => {
					this.companies = response.data.filter(company => company.is_system_insurance);
					this.checkNameAlreadyUsed();
				})
				.catch(error => {});
		},
		getCompanyProfileData(id) {
			InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.insurancesIdGet(id)
				.then(response => {
					this.isLoading = false;
					this.model = response.data;
					if (!this.isSystemInsurance) {
						this.updateInsurances();
					}
				})
				.catch(error => {
					this.isLoading = false;
				});
		},
	},
};
</script>
